import React, { useState } from 'react';
import { Container, Card, Button, Table, Row, Col, Form, Modal, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import databaseService from '../services/database';

const StrengthGraph = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state including testRequestId
  const { formData, testData, testIndex, observationsData, testRequestId } = location.state || {};
  
  // Log received data for debugging
  console.log('🔍 StrengthGraph - Received Data:');
  console.log('  testRequestId:', testRequestId);
  console.log('  formData:', formData);
  console.log('  testData:', testData);
  console.log('  observationsData:', observationsData);
  console.log('  location.state:', location.state);
  
  // Removed reviewer state - not needed on this page
  
  // Strength values lookup table based on concrete grade (New Concrete Grade 16-March-2026)
  const strengthValuesLookup = {
    'M-15': { required_7: 9, required_14: 13, required_28: 19 },
    'M-20': { required_7: 12, required_14: 17, required_28: 24 },
    'M-25': { required_7: 15, required_14: 21.5, required_28: 29 },
    'M-30': { required_7: 18, required_14: 25.5, required_28: 34 },
    'M-35': { required_7: 21, required_14: 30, required_28: 39 },
    'M-40': { required_7: 24, required_14: 34, required_28: 44 },
    'M-45': { required_7: 27, required_14: 38.5, required_28: 49 },
    'M-50': { required_7: 30, required_14: 42.5, required_28: 54 }
  };
  
  const [strengthData, setStrengthData] = useState({
    selectedGrade: '',
    required_7: '',
    actual_7: '',
    required_14: '',
    actual_14: '',
    required_28: '',
    actual_28: '',
    obs_strength_duration: '',
    obs_test_results: '',
    obs_weight: '',
    obs_failure_pattern: '',
    obs_bonding: '',
    obs_strength_criteria: ''
  });
  
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  const [autoDetectMessage, setAutoDetectMessage] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is initial load (prevent auto-save)
  const [averageStrength, setAverageStrength] = useState(null); // Store average strength for display

  // Normalize grade format utility function
  const normalizeGrade = (grade) => {
    if (!grade) return null;
    const gradeStr = String(grade).trim().toUpperCase();
    // If already in M-XX format, return as-is
    if (/^M-\d+$/.test(gradeStr)) {
      return gradeStr;
    }
    // If in MXX format (no hyphen), add hyphen
    if (/^M\d+$/.test(gradeStr)) {
      return gradeStr.replace(/^M(\d+)$/, 'M-$1');
    }
    // Return as-is if format doesn't match
    return gradeStr;
  };

  // Immediately set grade from passed data if available (runs once on mount)
  React.useEffect(() => {
    // Try to get grade from passed data immediately
    let immediateGrade = null;
    
    if (testData && testData.grade) {
      immediateGrade = testData.grade;
    } else if (testData && testData.cubeTests && testData.cubeTests[0] && testData.cubeTests[0].grade) {
      immediateGrade = testData.cubeTests[0].grade;
    } else if (formData && formData.cubeTests && formData.cubeTests[0] && formData.cubeTests[0].grade) {
      immediateGrade = formData.cubeTests[0].grade;
    } else if (testData && testData.concrete_tests && testData.concrete_tests[0] && testData.concrete_tests[0].grade) {
      immediateGrade = testData.concrete_tests[0].grade;
    } else if (formData && formData.concrete_tests && formData.concrete_tests[0] && formData.concrete_tests[0].grade) {
      immediateGrade = formData.concrete_tests[0].grade;
    }
    
    if (immediateGrade && !strengthData.selectedGrade) {
      const normalized = normalizeGrade(immediateGrade);
      console.log('🚀 IMMEDIATE: Setting grade from passed data:', immediateGrade, '→', normalized);
      if (normalized && strengthValuesLookup[normalized]) {
        const strengthValues = strengthValuesLookup[normalized];
        setStrengthData(prevData => ({
          ...prevData,
          selectedGrade: normalized,
          required_7: prevData.required_7 || strengthValues.required_7.toString(),
          required_14: prevData.required_14 || strengthValues.required_14.toString(),
          required_28: prevData.required_28 || strengthValues.required_28.toString()
        }));
        console.log('✅ IMMEDIATE: Grade set in dropdown:', normalized);
      }
    }
  }, []); // Run only once on mount

  // Removed reviewer fetching - not needed on this page
  
  // Intercept browser back button
  React.useEffect(() => {
    const handlePopState = (event) => {
      // Navigate directly to dashboard without popup
      navigate('/dashboard', { replace: true });
    };

    // Clear history and add event listener
    window.history.replaceState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);
  
  // Auto-populate required strength values based on concrete grade
  React.useEffect(() => {
    const autoPopulateRequiredStrength = async () => {
      setIsAutoDetecting(true);
      setAutoDetectMessage('🔍 Auto-detecting concrete grade...');
      
      // Get the concrete grade from testData
      let concreteGrade = null;
      
      console.log('🔍 Debug - Looking for grade in:');
      console.log('  testData:', testData);
      console.log('  formData:', formData);
      console.log('  testIndex:', testIndex);
      console.log('  testData?.cubeTests:', testData?.cubeTests);
      console.log('  testData?.concrete_tests:', testData?.concrete_tests);
      console.log('  formData?.cubeTests:', formData?.cubeTests);
      console.log('  formData?.concrete_tests:', formData?.concrete_tests);
      
      // Try multiple paths to find the grade
      if (testData && testData.grade) {
        concreteGrade = testData.grade;
        console.log('✅ Found grade in testData.grade:', concreteGrade);
      } else if (testData && testData.cubeTests && testData.cubeTests[testIndex] && testData.cubeTests[testIndex].grade) {
        concreteGrade = testData.cubeTests[testIndex].grade;
        console.log('✅ Found grade in testData.cubeTests[testIndex].grade:', concreteGrade);
      } else if (formData && formData.cubeTests && formData.cubeTests[testIndex] && formData.cubeTests[testIndex].grade) {
        concreteGrade = formData.cubeTests[testIndex].grade;
        console.log('✅ Found grade in formData.cubeTests[testIndex].grade:', concreteGrade);
      } else if (testData && testData.concrete_tests && testData.concrete_tests[testIndex] && testData.concrete_tests[testIndex].grade) {
        concreteGrade = testData.concrete_tests[testIndex].grade;
        console.log('✅ Found grade in testData.concrete_tests[testIndex].grade:', concreteGrade);
      } else if (formData && formData.concrete_tests && formData.concrete_tests[testIndex] && formData.concrete_tests[testIndex].grade) {
        concreteGrade = formData.concrete_tests[testIndex].grade;
        console.log('✅ Found grade in formData.concrete_tests[testIndex].grade:', concreteGrade);
      } else if (testData && testData.cubeTests && testData.cubeTests[0] && testData.cubeTests[0].grade) {
        concreteGrade = testData.cubeTests[0].grade;
        console.log('✅ Found grade in testData.cubeTests[0].grade (fallback):', concreteGrade);
      } else if (formData && formData.cubeTests && formData.cubeTests[0] && formData.cubeTests[0].grade) {
        concreteGrade = formData.cubeTests[0].grade;
        console.log('✅ Found grade in formData.cubeTests[0].grade (fallback):', concreteGrade);
      } else if (testData && testData.concrete_tests && testData.concrete_tests[0] && testData.concrete_tests[0].grade) {
        concreteGrade = testData.concrete_tests[0].grade;
        console.log('✅ Found grade in testData.concrete_tests[0].grade (fallback):', concreteGrade);
      } else if (formData && formData.concrete_tests && formData.concrete_tests[0] && formData.concrete_tests[0].grade) {
        concreteGrade = formData.concrete_tests[0].grade;
        console.log('✅ Found grade in formData.concrete_tests[0].grade (fallback):', concreteGrade);
      }
      
      // If grade not found in passed data, try to fetch from API
      if (!concreteGrade && testRequestId) {
        console.log('🔍 Grade not found in passed data, fetching from API...');
        try {
          const data = await databaseService.getTestRequestPDFData(testRequestId);
          console.log('📊 API Data Response:', data);
          
          if (data.main_test) {
            // Try multiple paths in API data
            if (data.main_test.grade) {
              concreteGrade = data.main_test.grade;
              console.log('✅ Found grade in data.main_test.grade:', concreteGrade);
            } else if (data.main_test.concrete_tests && data.main_test.concrete_tests[testIndex] && data.main_test.concrete_tests[testIndex].grade) {
              concreteGrade = data.main_test.concrete_tests[testIndex].grade;
              console.log('✅ Found grade in API data concrete_tests[testIndex]:', concreteGrade);
            } else if (data.main_test.concrete_tests && data.main_test.concrete_tests[0] && data.main_test.concrete_tests[0].grade) {
              concreteGrade = data.main_test.concrete_tests[0].grade;
              console.log('✅ Found grade in API data concrete_tests[0]:', concreteGrade);
            }
          }
        } catch (error) {
          console.log('❌ Error fetching grade from API:', error);
        }
      }
      
      // Skip if grade is already set AND matches the found grade (to prevent unnecessary updates)
      if (strengthData.selectedGrade && strengthData.selectedGrade !== '' && concreteGrade) {
        const normalizeGrade = (grade) => {
          if (!grade) return null;
          const gradeStr = String(grade).trim().toUpperCase();
          if (/^M-\d+$/.test(gradeStr)) return gradeStr;
          if (/^M\d+$/.test(gradeStr)) return gradeStr.replace(/^M(\d+)$/, 'M-$1');
          return gradeStr;
        };
        const currentNormalized = normalizeGrade(strengthData.selectedGrade);
        const foundNormalized = normalizeGrade(concreteGrade);
        if (currentNormalized === foundNormalized) {
          console.log('🔍 Grade already set and matches found grade, skipping auto-detection:', strengthData.selectedGrade);
          setIsAutoDetecting(false);
          setAutoDetectMessage('');
          return;
        }
      }
      
      console.log('🔍 Auto-populate - Final Concrete Grade:', concreteGrade);
      
      // Normalize grade format using shared utility function
      const normalizedGrade = normalizeGrade(concreteGrade);
      console.log('🔍 Normalized grade:', concreteGrade, '→', normalizedGrade);
      
      if (normalizedGrade && strengthValuesLookup[normalizedGrade]) {
        const strengthValues = strengthValuesLookup[normalizedGrade];
        console.log('✅ Auto-populating required strength values for grade:', normalizedGrade, strengthValues);
        
        setStrengthData(prevData => ({
          ...prevData,
          selectedGrade: normalizedGrade,
          required_7: strengthValues.required_7.toString(),
          required_14: strengthValues.required_14.toString(),
          required_28: strengthValues.required_28.toString()
        }));
        
        setAutoDetectMessage(`✅ Auto-detected grade: ${normalizedGrade} - Required strength values populated!`);
        setTimeout(() => {
          setIsAutoDetecting(false);
          setAutoDetectMessage('');
        }, 3000);
      } else {
        console.log('⚠️ No strength values found for grade:', normalizedGrade);
        console.log('Original grade:', concreteGrade);
        console.log('Available grades in lookup:', Object.keys(strengthValuesLookup));
        
        // If grade was found but not normalized correctly, try to set it anyway
        if (normalizedGrade) {
          console.log('⚠️ Grade format mismatch. Setting grade in dropdown but values may not auto-populate.');
          setStrengthData(prevData => ({
            ...prevData,
            selectedGrade: normalizedGrade
          }));
        }
        
        setAutoDetectMessage('⚠️ Grade not auto-detected. Please select grade manually from dropdown.');
        setTimeout(() => {
          setIsAutoDetecting(false);
          setAutoDetectMessage('');
        }, 5000);
      }
    };

    autoPopulateRequiredStrength();
  }, [testData, formData, testIndex, testRequestId]);
  
  // Ensure selectedGrade persists and doesn't reset
  React.useEffect(() => {
    console.log('🔍 Current selectedGrade state:', strengthData.selectedGrade);
    if (strengthData.selectedGrade && strengthData.selectedGrade !== '') {
      console.log('✅ Grade is selected and persisted:', strengthData.selectedGrade);
    }
  }, [strengthData.selectedGrade]);

  // Display values from previous page (observationsData) - DISPLAY ONLY, NOT AUTO-SAVE
  // These values are fetched from previous page for display purposes
  // They will ONLY be saved when user clicks "Generate Graph" or "Complete Test"
  React.useEffect(() => {
    // CRITICAL: Mark that we're in initial load phase - NO AUTO-SAVING
    setIsInitialLoad(true);
    console.log('🚫 INITIAL LOAD: Auto-saving is DISABLED. Values will only be saved on button click.');
    
    // Get averageStrength from previous page for display only
    const avgStrength = 
      observationsData?.average_strength || 
      testData?.averageStrength || 
      testData?.average_strength ||
      formData?.averageStrength ||
      formData?.average_strength ||
      null;
    
    console.log('🔍 Average Compressive Strength from previous page:', avgStrength);
    
    // Store average strength in state for display banner ONLY
    // DO NOT auto-populate actual_7 - user must enter values manually
    if (avgStrength) {
      setAverageStrength(avgStrength);
      console.log('✅ Average strength stored for banner display:', avgStrength);
      console.log('⚠️ NOT auto-populating actual_7 - user must enter values manually');
    }
    
    // Mark initial load as complete after a short delay (allows all fetches to complete)
    setTimeout(() => {
      setIsInitialLoad(false);
      console.log('✅ INITIAL LOAD COMPLETE: Auto-saving can now happen on button clicks only.');
    }, 1000);
  }, [observationsData, testData, formData]); // Run once when component receives data from previous page

  // Fetch saved strength graph data when component mounts - DISPLAY ONLY, NOT AUTO-SAVE
  React.useEffect(() => {
    const fetchSavedData = async () => {
      if (!testRequestId) return;
      
      // CRITICAL: Ensure we're in initial load phase - NO AUTO-SAVING during fetch
      console.log('🚫 FETCHING DATA: Auto-saving is DISABLED. Only displaying values.');
      
      try {
        console.log('🔄 Fetching saved strength graph data for test (DISPLAY ONLY):', testRequestId);
        
        // Use the database service to fetch PDF data
        const data = await databaseService.getTestRequestPDFData(testRequestId);
        
        console.log('📊 PDF Data Response (for display only):', data);
        
        if (data.main_test) {
          const testResults = data.main_test.test_results_json || {};
          const observations = data.main_test.observations_json || {};
          
          console.log('✅ Found saved test results (DISPLAY ONLY):', testResults);
          console.log('✅ Found saved observations (DISPLAY ONLY):', observations);
          
          // CRITICAL FIX: Only read from observations_json (single source of truth)
          // Do NOT read from test_results_json.strength_data to avoid duplicates
          // Strength data is stored ONLY in observations_json
          
          // Get grade from database and normalize it
          const gradeFromDB = data.main_test.grade || null;
          const normalizedGradeFromDB = normalizeGrade(gradeFromDB);
          console.log('🔍 Grade from database (for display):', gradeFromDB, '→ Normalized:', normalizedGradeFromDB);
          
          // CRITICAL FIX: DO NOT populate actual values from database when fetching
          // Actual values should remain empty (showing placeholders) after fetching
          // Only populate required values and observation fields, NOT actual strength values
          setStrengthData(prevData => {
            let updatedData = {
              ...prevData, // Preserve existing data
              // Set grade from database if not already set
              selectedGrade: prevData.selectedGrade || normalizedGradeFromDB || '',
              // Only populate required values from database
              required_7: observations.required_7 || prevData.required_7 || '',
              required_14: observations.required_14 || prevData.required_14 || '',
              required_28: observations.required_28 || prevData.required_28 || '',
              // CRITICAL FIX: Load actual values from database if they exist
              // Preserve user-entered values if they exist in current session, otherwise load from database
              // Load actual values from database if they exist
              actual_7: (prevData.actual_7 && prevData.actual_7.toString().trim() !== '') 
                ? prevData.actual_7 
                : (observations.actual_7 !== null && observations.actual_7 !== undefined && observations.actual_7 !== '') 
                  ? observations.actual_7.toString() 
                  : '',
              actual_14: (prevData.actual_14 && prevData.actual_14.toString().trim() !== '') 
                ? prevData.actual_14 
                : (observations.actual_14 !== null && observations.actual_14 !== undefined && observations.actual_14 !== '') 
                  ? observations.actual_14.toString() 
                  : '',
              actual_28: (prevData.actual_28 && prevData.actual_28.toString().trim() !== '') 
                ? prevData.actual_28 
                : (observations.actual_28 !== null && observations.actual_28 !== undefined && observations.actual_28 !== '') 
                  ? observations.actual_28.toString() 
                  : '',
              // Populate observation fields (preserve empty string from DB; do not fall back via ||)
              obs_strength_duration: 'obs_strength_duration' in observations
                ? (observations.obs_strength_duration ?? '')
                : (prevData.obs_strength_duration ?? ''),
              obs_test_results: 'obs_test_results' in observations
                ? (observations.obs_test_results ?? '')
                : (prevData.obs_test_results ?? ''),
              obs_weight: 'obs_weight' in observations
                ? (observations.obs_weight ?? '')
                : (prevData.obs_weight ?? ''),
              obs_failure_pattern: 'obs_failure_pattern' in observations
                ? (observations.obs_failure_pattern ?? '')
                : (prevData.obs_failure_pattern ?? ''),
              obs_bonding: 'obs_bonding' in observations
                ? (observations.obs_bonding ?? '')
                : (prevData.obs_bonding ?? ''),
              obs_strength_criteria: 'obs_strength_criteria' in observations
                ? (observations.obs_strength_criteria ?? '')
                : (prevData.obs_strength_criteria ?? '')
            };
            
            // If grade was found in DB and required strength values are missing, populate them
            if (normalizedGradeFromDB && strengthValuesLookup[normalizedGradeFromDB]) {
              const strengthValues = strengthValuesLookup[normalizedGradeFromDB];
              if (!updatedData.required_7 || updatedData.required_7 === '') {
                updatedData.required_7 = strengthValues.required_7.toString();
              }
              if (!updatedData.required_14 || updatedData.required_14 === '') {
                updatedData.required_14 = strengthValues.required_14.toString();
              }
              if (!updatedData.required_28 || updatedData.required_28 === '') {
                updatedData.required_28 = strengthValues.required_28.toString();
              }
              console.log('✅ Populated required strength values from grade (DISPLAY ONLY):', normalizedGradeFromDB);
            }
            
            return updatedData;
          });
          
          console.log('✅ Strength data DISPLAYED from database (NOT AUTO-SAVED - will only save on button click)');
        } else {
          console.log('⚠️ No main_test data found in response');
        }
      } catch (error) {
        console.log('⚠️ No saved data found (this is OK for new tests):', error.message);
      }
    };

    fetchSavedData();
  }, [testRequestId, observationsData, testData, formData]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // CRITICAL DEBUG: Log when actual_14 is being changed
    if (name === 'actual_14') {
      console.log('🔍 actual_14 INPUT CHANGED:', {
        old_value: strengthData.actual_14,
        new_value: value,
        value_type: typeof value,
        value_length: value?.length,
        is_empty: value === '',
        will_update: true
      });
    }
    setStrengthData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateGraph = async () => {
    // Only validate test request ID - everything else is optional
    if (!testRequestId) {
      setErrorMessage('Error: Test Request ID is missing. Cannot save graph data.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      return;
    }

    // CRITICAL: Ensure we're not in initial load phase
    if (isInitialLoad) {
      console.log('⏳ Waiting for initial load to complete...');
      setTimeout(() => generateGraph(), 500);
      return;
    }

    setIsGenerating(true);
    
    try {
      // CRITICAL: Only save the CURRENT values in the fields
      // This ensures that only one value per field is saved (not fetched + manual)
      // Each field is independent - whatever value is currently displayed is what gets saved
      // CRITICAL FIX: Convert empty strings to null so backend can distinguish between empty and not set
      const dataToSave = {
        ...strengthData,
        // Ensure we're saving exactly what's in the form fields right now
        // CRITICAL: Send actual values as-is - backend will handle conversion
        // Don't convert to null here - let backend's safe_float handle it
        // This ensures numeric values are properly saved
        actual_7: strengthData.actual_7,
        actual_14: strengthData.actual_14,
        actual_28: strengthData.actual_28,
      };
      
      console.log('💾 USER CLICKED "GENERATE GRAPH" - NOW SAVING CURRENT VALUES:', dataToSave);
      console.log('💾 CRITICAL - Only saving current field values (one value per field):', {
        actual_7: dataToSave.actual_7,
        actual_14: dataToSave.actual_14,
        actual_28: dataToSave.actual_28,
        required_7: dataToSave.required_7,
        required_14: dataToSave.required_14,
        required_28: dataToSave.required_28
      });
      console.log('💾 CRITICAL - Type checks:', {
        actual_7_type: typeof dataToSave.actual_7,
        actual_7_value: dataToSave.actual_7,
        actual_14_type: typeof dataToSave.actual_14,
        actual_14_value: dataToSave.actual_14,
        actual_28_type: typeof dataToSave.actual_28,
        actual_28_value: dataToSave.actual_28
      });
      console.log('💾 CRITICAL - 14 DAYS (actual_14) DEBUG:', {
        strengthData_actual_14: strengthData.actual_14,
        strengthData_actual_14_type: typeof strengthData.actual_14,
        strengthData_actual_14_length: strengthData.actual_14?.length,
        dataToSave_actual_14: dataToSave.actual_14,
        dataToSave_actual_14_type: typeof dataToSave.actual_14,
        is_empty_string: dataToSave.actual_14 === '',
        is_null: dataToSave.actual_14 === null,
        is_undefined: dataToSave.actual_14 === undefined,
        will_be_sent: dataToSave.actual_14 !== '' && dataToSave.actual_14 !== null && dataToSave.actual_14 !== undefined
      });
      console.log('📍 URL:', `${API_BASE_URL}/strength-graph/${testRequestId}`);
      
      // First, test backend connectivity
      console.log('🔍 Testing backend connectivity...');
      try {
        await axios.get(API_BASE_URL.replace('/api', '/'));
        console.log('✅ Backend is reachable');
      } catch (connectError) {
        console.error('❌ Backend connectivity test failed:', connectError);
        throw new Error('Cannot connect to backend server. Make sure Flask is running on port 5000.');
      }
      
      // Save to database using database service - ONLY CURRENT VALUES
      console.log('💾 SAVING strength graph data (user-initiated save)...');
      const response = await databaseService.saveStrengthGraphData(testRequestId, dataToSave);
      
      console.log('✅ Strength graph data saved successfully (user-initiated):', response);
      
      // Show success and display graph
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
        setShowGraph(true);
      }, 2000);
      
    } catch (error) {
      console.error('❌ Full error object:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      
      let errorMsg = 'Failed to save strength graph data. ';
      
      if (error.response) {
        // Backend returned an error
        errorMsg += `Server error: ${error.response.data?.error || error.response.statusText}`;
      } else if (error.request) {
        // Request made but no response
        errorMsg += `Backend server is not responding. Please make sure Flask is running on ${API_BASE_URL.replace('/api', '')}`;
      } else {
        // Something else
        errorMsg += error.message;
      }
      
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 5000); // Show for 5 seconds
    } finally {
      setIsGenerating(false);
    }
  };

  // Prepare chart data - reads from single source (strengthData state)
  const getChartData = () => {
    // CRITICAL: Only read from strengthData state (single source of truth)
    // This ensures only ONE value per field is used for the graph
    const requiredValues = [
      parseFloat(strengthData.required_7) || 0,
      parseFloat(strengthData.required_14) || 0,
      parseFloat(strengthData.required_28) || 0
    ];
    const actualValues = [
      parseFloat(strengthData.actual_7) || 0,
      parseFloat(strengthData.actual_14) || 0,
      parseFloat(strengthData.actual_28) || 0
    ];
    
    // Debug log to verify single values (not duplicates)
    console.log('📊 Graph Data - Single values per field:', {
      actual_7: actualValues[0],
      actual_14: actualValues[1],
      actual_28: actualValues[2],
      required_7: requiredValues[0],
      required_14: requiredValues[1],
      required_28: requiredValues[2]
    });

    const maxValue = Math.max(...requiredValues, ...actualValues, 35);
    const allValues = [...requiredValues, ...actualValues];
    const hasValueAbove50 = allValues.some(value => value > 50);
    
    // Dynamic y-axis calculation with conditional intervals
    let yMax, interval;
    if (hasValueAbove50) {
      // If any value > 50, use 10-unit intervals
      interval = 10;
      if (maxValue <= 55) {
        yMax = 60;
      } else {
        yMax = Math.ceil(maxValue / 10) * 10;
      }
    } else {
      // If all values ≤ 50, use 5-unit intervals
      interval = 5;
      yMax = Math.ceil(maxValue / 5) * 5;
    }
    
    // Generate y-axis labels with appropriate intervals
    const yAxisLabels = [];
    for (let i = 0; i <= yMax; i += interval) {
      yAxisLabels.push(i);
    }
    
    return {
      data: [
        { name: '7 days strength', required: requiredValues[0], actual: actualValues[0] },
        { name: '14 days strength', required: requiredValues[1], actual: actualValues[1] },
        { name: '28 days strength', required: requiredValues[2], actual: actualValues[2] }
      ],
      yMax,
      yAxisLabels
    };
  };


  return (
    <div style={{ backgroundColor: '#1C2333', minHeight: '100vh', padding: '20px 0' }}>
      <Container>
        <style>{`
          .table th {
            background-color: #1C2333 !important;
            color: #FFD700 !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
          .table td {
            background-color: #1C2333 !important;
            color: white !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
          .bg-secondary {
            background-color: #1C2333 !important;
            border-bottom: 2px solid #FFA500 !important;
          }
          .bg-dark {
            background-color: #1C2333 !important;
          }
          .form-control {
            background-color: #1C2333 !important;
            color: white !important;
            border: 2px solid rgba(255, 255, 255, 0.2) !important;
          }
          .form-control:focus {
            background-color: #1C2333 !important;
            color: white !important;
            border-color: #FFA500 !important;
            box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
          }
          .form-control::placeholder {
            color: #adb5bd !important;
          }
          .form-select {
            background-color: #1C2333 !important;
            color: white !important;
            border: 2px solid rgba(255, 255, 255, 0.2) !important;
          }
          .form-select:focus {
            background-color: #1C2333 !important;
            color: white !important;
            border-color: #FFA500 !important;
            box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
          }
          .form-select option {
            background-color: #1C2333 !important;
            color: white !important;
          }
          .card {
            background-color: #1C2333 !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 15px !important;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
          }
          .card-header {
            background-color: #1C2333 !important;
            border-bottom: 2px solid #FFA500 !important;
            color: white !important;
          }
          .card-body {
            background-color: #1C2333 !important;
            color: white !important;
          }
          
          /* Bar Chart CSS */
          .chart-bar-required {
            width: 80px;
            background-color: #4682B4;
          }
          
          .chart-bar-actual {
            width: 80px;
            background-color: #FFA500;
          }
          
          .chart-bar-group {
            display: flex;
            gap: 5px;
            align-items: flex-end;
          }
          
          .chart-data-labels {
            margin-top: 3px;
            font-size: 9pt;
            text-align: center;
            color: #000;
            display: flex;
            flex-direction: column;
            line-height: 1.4;
          }
          
          /* Responsive improvements */
          @media (max-width: 768px) {
            .table-responsive {
              font-size: 0.8rem;
            }
            .table th, .table td {
              padding: 0.5rem 0.25rem;
            }
            .form-control, .form-select {
              font-size: 0.8rem;
            }
            .card-body {
              padding: 1rem;
            }
            .btn {
              font-size: 0.9rem;
            }
          }
          
          @media (max-width: 576px) {
            .table-responsive {
              font-size: 0.7rem;
            }
            .table th, .table td {
              padding: 0.25rem 0.1rem;
            }
            .form-control, .form-select {
              font-size: 0.7rem;
              padding: 0.25rem 0.5rem;
            }
            .card-body {
              padding: 0.75rem;
            }
            .btn {
              font-size: 0.8rem;
              padding: 0.5rem 1rem;
            }
            .badge {
              font-size: 0.6rem;
              padding: 0.25rem 0.5rem;
            }
          }
          
          /* Blink Animation for Average Strength Banner */
          @keyframes blink {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.7;
              transform: scale(1.02);
            }
          }
        `}</style>

        <Card className="shadow mb-4">
          <Card.Header className="text-white" style={{ backgroundColor: '#1C2333', borderBottom: '2px solid #FFA500', padding: '15px' }}>
            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mb-2 mb-lg-0">
                <img 
                  src="/logo.png" 
                  alt="Vitrag Associates Logo" 
                  height="40" 
                  className="me-2 mb-2 mb-sm-0"
                />
                <div>
                  <h3 className="mb-0" style={{ fontSize: 'clamp(1.2rem, 4vw, 1.5rem)' }}>Concrete Cube Strength Graph</h3>
                  <div className="mt-2 d-flex flex-column flex-sm-row flex-wrap gap-2">
                    <span className="badge fs-6 p-2" style={{ backgroundColor: '#FFA500', fontSize: 'clamp(0.7rem, 2.5vw, 0.9rem)' }}>
                      Reference: {testData?.referenceNumber || formData?.referenceNumber || 'N/A'}
                    </span>
                    <span className="badge fs-6 p-2" style={{ backgroundColor: '#6c757d', fontSize: 'clamp(0.7rem, 2.5vw, 0.9rem)' }}>
                      ID: {testRequestId || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Edit Observation Button in Header - Small Right Corner */}
              <Button 
                variant="warning" 
                size="sm"
                onClick={() => {
                  console.log('✏️ Edit Observation button clicked!');
                  console.log('✏️ testRequestId:', testRequestId);
                  console.log('✏️ observationsData:', observationsData);
                  
                  // Navigate to TestObservations page with editMode flag (same as view-sample page)
                  navigate(`/test-observations/${testRequestId}`, { 
                    state: { 
                      testRequestId: testRequestId,
                      testRequest: { 
                        id: parseInt(testRequestId),
                        ...formData, 
                        ...testData
                      },
                      testData: testData || testData?.cubeTests?.[0],
                      testIndex: 0,
                      editMode: true // Important: Set editMode to true
                    } 
                  });
                }}
                className="align-self-start align-self-md-end"
                style={{ 
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  padding: 'clamp(4px, 1vw, 6px) clamp(8px, 2vw, 12px)',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto'
                }}
              >
                <i className="fas fa-edit me-1"></i> 
                <span className="d-none d-lg-inline">Edit Observation</span>
                <span className="d-inline d-lg-none">Edit</span>
              </Button>
            </div>
          </Card.Header>
        
        {/* Blinking Average Strength Banner */}
        {averageStrength && (
          <div 
            style={{
              backgroundColor: '#FFA500',
              color: '#000',
              padding: '15px 20px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: 'clamp(1rem, 3vw, 1.3rem)',
              borderBottom: '2px solid #FFD700',
              animation: 'blink 1.5s infinite',
              boxShadow: '0 2px 10px rgba(255, 165, 0, 0.5)'
            }}
          >
            <i className="fas fa-chart-line me-2"></i>
            Average Compressive Strength: <strong>{typeof averageStrength === 'number' ? averageStrength.toFixed(2) : averageStrength}</strong> N/mm²
          </div>
        )}
        
        <Card.Body>
          <Row className="mb-4">
            <Col xs={12}>
              <Form>
                <h5 className="mb-3 text-center fw-bold" style={{ 
                  color: '#FFD700', 
                  backgroundColor: '#1C2333', 
                  padding: '15px',
                  borderBottom: '2px solid #FFA500',
                  borderRadius: '10px 10px 0 0',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontSize: 'clamp(1rem, 3vw, 1.25rem)'
                }}>Enter Actual Cube Strength Values</h5>
                
                {/* Auto-detect Status */}
                {isAutoDetecting && (
                  <div className="mb-3">
                    <Alert variant="info" className="text-center">
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        {autoDetectMessage}
                      </div>
                    </Alert>
                  </div>
                )}
                
                {/* Success/Error Message */}
                {autoDetectMessage && !isAutoDetecting && (
                  <div className="mb-3">
                    <Alert variant={autoDetectMessage.includes('✅') ? 'success' : 'warning'} className="text-center">
                      {autoDetectMessage}
                    </Alert>
                  </div>
                )}

                {/* Grade Selector */}
                <div className="mb-3">
                  <Row>
                    <Col md={6}>
                      <Form.Label className="text-white fw-bold">Select Concrete Grade:</Form.Label>
                      <Form.Select
                        value={strengthData.selectedGrade || ''}
                        onChange={(e) => {
                          const selectedGrade = e.target.value;
                          console.log('🔍 Manual grade selection:', selectedGrade);
                          
                          // Prevent clearing the grade if it was auto-detected
                          if (selectedGrade === '' && strengthData.selectedGrade && strengthData.selectedGrade !== '') {
                            console.log('⚠️ Attempting to clear auto-detected grade, preventing...');
                            return;
                          }
                          
                          setStrengthData(prevData => ({
                            ...prevData,
                            selectedGrade: selectedGrade
                          }));
                          
                          // Auto-populate required strength values
                          if (selectedGrade && strengthValuesLookup[selectedGrade]) {
                            const strengthValues = strengthValuesLookup[selectedGrade];
                            console.log('✅ Manual selection - populating values for grade:', selectedGrade, strengthValues);
                            
                            setStrengthData(prevData => ({
                              ...prevData,
                              selectedGrade: selectedGrade,
                              required_7: strengthValues.required_7.toString(),
                              required_14: strengthValues.required_14.toString(),
                              required_28: strengthValues.required_28.toString()
                            }));
                          }
                        }}
                        className="form-select-sm"
                      >
                        <option value="">Select Grade</option>
                        <option value="M-15">M-15</option>
                        <option value="M-20">M-20</option>
                        <option value="M-25">M-25</option>
                        <option value="M-30">M-30</option>
                        <option value="M-35">M-35</option>
                        <option value="M-40">M-40</option>
                        <option value="M-45">M-45</option>
                        <option value="M-50">M-50</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <div className="text-white">
                        <small>Auto-populates required strength values based on grade selection</small>
                        {strengthData.selectedGrade && (
                          <div className="mt-2">
                            <small className="text-success">
                              ✅ Currently selected: <strong>{strengthData.selectedGrade}</strong>
                            </small>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className="table-responsive">
                  <Table bordered className="table-sm" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#1C2333' }}>
                        <th className="text-center" style={{ color: '#FFD700' }}>Curing days</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Required strength</th>
                        <th className="text-center" style={{ color: '#FFD700' }}>Actual Cube strength</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="fw-semibold">7 days strength</td>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="required_7"
                            value={strengthData.required_7}
                            onChange={handleInputChange}
                            placeholder="Required strength"
                            className="form-control-sm"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            name="actual_7"
                            value={strengthData.actual_7}
                            onChange={handleInputChange}
                            placeholder="Actual strength"
                            className="form-control-sm"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">14 days strength</td>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="required_14"
                            value={strengthData.required_14}
                            onChange={handleInputChange}
                            placeholder="Required strength"
                            className="form-control-sm"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            name="actual_14"
                            value={strengthData.actual_14}
                            onChange={handleInputChange}
                            placeholder="Actual strength"
                            className="form-control-sm"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-semibold">28 days strength</td>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            name="required_28"
                            value={strengthData.required_28}
                            onChange={handleInputChange}
                            placeholder="Required strength"
                            className="form-control-sm"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            name="actual_28"
                            value={strengthData.actual_28}
                            onChange={handleInputChange}
                            placeholder="Actual strength"
                            className="form-control-sm"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
                
                {/* Observations Table */}
                <Card className="mb-4 mt-4">
                  <Card.Header style={{ backgroundColor: '#1C2333', borderBottom: '2px solid #FFA500' }}>
                    <h5 className="mb-0 text-center fw-bold" style={{ color: '#FFD700', fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>Observations</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table bordered className="mb-0 table-sm" style={{ fontSize: 'clamp(0.75rem, 2.5vw, 0.9rem)' }}>
                        <tbody>
                          <tr>
                            <td className="text-center" style={{ width: '5%' }}>1.</td>
                            <td style={{ width: '70%' }}>
                              <span className="d-block">Compressive Strength acquired after specified duration</span>
                            </td>
                            <td style={{ width: '25%' }}>
                              <Form.Select
                                name="obs_strength_duration"
                                value={strengthData.obs_strength_duration}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center">2.</td>
                            <td>
                              <span className="d-block">Individual test results within ±15% of average strength</span>
                            </td>
                            <td>
                              <Form.Select
                                name="obs_test_results"
                                value={strengthData.obs_test_results}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center">3.</td>
                            <td>
                              <span className="d-block">Weight of cube</span>
                            </td>
                            <td>
                              <Form.Select
                                name="obs_weight"
                                value={strengthData.obs_weight}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center">4.</td>
                            <td>
                              <span className="d-block">Type of failure Pattern as per IS 516(Part-1/Sec-1): - 2021 Fig.1</span>
                            </td>
                            <td>
                              <Form.Select
                                name="obs_failure_pattern"
                                value={strengthData.obs_failure_pattern}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center">5.</td>
                            <td>
                              <span className="d-block">Bonding between Aggregates and cement paste.</span>
                            </td>
                            <td>
                              <Form.Select
                                name="obs_bonding"
                                value={strengthData.obs_bonding}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                          <tr>
                            <td className="text-center">6.</td>
                            <td>
                              <span className="d-block">Compressive Strength as per acceptance criteria as Cl.16.1 of IS 456:2000</span>
                            </td>
                            <td>
                              <Form.Select
                                name="obs_strength_criteria"
                                value={strengthData.obs_strength_criteria}
                                onChange={handleInputChange}
                                className="form-select-sm"
                              >
                                <option value="">---------</option>
                                <option value="Satisfactory">Satisfactory</option>
                                <option value="Unsatisfactory">Unsatisfactory</option>
                              </Form.Select>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
                
                <div className="text-center mt-3">
                  {/* Generate Graph Button */}
                  <Button 
                    variant="success" 
                    size="lg"
                    onClick={generateGraph}
                    disabled={isGenerating}
                    className="w-100 w-md-auto"
                    style={{ 
                      fontSize: 'clamp(0.9rem, 3vw, 1.1rem)',
                      padding: 'clamp(8px, 2vw, 12px) clamp(16px, 4vw, 24px)'
                    }}
                  >
                    {isGenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        <span className="d-none d-sm-inline">Generating Graph...</span>
                        <span className="d-inline d-sm-none">Generating...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chart-bar me-2"></i> 
                        <span className="d-none d-sm-inline">Generate Graph</span>
                        <span className="d-inline d-sm-none">Generate</span>
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>

          {/* Loading Indicator for Graph Generation */}
          {isGenerating && (
            <Row className="mt-4">
              <Col xs={12}>
                <Card style={{ 
                  backgroundColor: '#1C2333', 
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '15px'
                }}>
                  <Card.Body className="text-center" style={{ backgroundColor: '#1C2333' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                      <div>
                        <div 
                          className="spinner-border" 
                          role="status" 
                          style={{ width: '4rem', height: '4rem', color: '#FFA500' }}
                        >
                          <span className="visually-hidden">Generating...</span>
                        </div>
                        <h4 className="mt-3" style={{ color: '#ffffff' }}>Generating Graph...</h4>
                        <p style={{ color: '#adb5bd' }}>Please wait while we create your strength comparison chart</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Graph Display Area */}
          {showGraph && !isGenerating && (
            <Row className="mt-4">
              <Col xs={12}>
                <Card style={{ 
                  backgroundColor: '#1C2333', 
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '15px'
                }}>
                  <Card.Body className="text-center" style={{ backgroundColor: '#1C2333' }}>
                    <div className="d-flex justify-content-center">
                      <div className="p-2 p-md-4 rounded" style={{ backgroundColor: 'white', width: '100%', maxWidth: '900px', border: '3px solid #000', overflow: 'auto' }}>
                        {/* Pure CSS Bar Chart */}
                        <div style={{ 
                          fontFamily: "'Times New Roman', Times, serif", 
                          padding: 'clamp(10px, 2vw, 20px)', 
                          position: 'relative',
                          minWidth: '600px'
                        }}>
                          {/* Graph Title */}
                          <div style={{ 
                            textAlign: 'center', 
                            fontSize: 'clamp(9pt, 2.5vw, 11pt)', 
                            color: '#666', 
                            marginBottom: 'clamp(15px, 3vw, 30px)' 
                          }}>
                            Graphical presentation of compressive strength
                          </div>
                          
                          {/* Chart Container */}
                          <div style={{ 
                            position: 'relative', 
                            height: 'clamp(300px, 50vw, 450px)', 
                            marginTop: 'clamp(10px, 2vw, 20px)', 
                            marginLeft: 'clamp(30px, 5vw, 50px)',
                            width: 'calc(100% - clamp(30px, 5vw, 50px))'
                          }}>
                            {/* Y-axis */}
                            <div style={{ 
                              position: 'absolute', 
                              left: 'clamp(40px, 8vw, 60px)', 
                              top: '0', 
                              height: 'calc(100% - 50px)', 
                              borderLeft: '2px solid #000' 
                            }}>
                              {/* Y-axis labels - dynamic with gap of 10 */}
                              {getChartData().yAxisLabels.reverse().map((value, i) => {
                                const totalLabels = getChartData().yAxisLabels.length - 1;
                                return (
                                  <div key={i} style={{ 
                                    position: 'absolute', 
                                    right: 'clamp(5px, 1vw, 10px)', 
                                    top: `${(i / totalLabels) * 100}%`, 
                                    transform: 'translateY(-50%)', 
                                    fontSize: 'clamp(7pt, 2vw, 9pt)', 
                                    color: '#000' 
                                  }}>
                                    {value}
                                  </div>
                                );
                              })}
                              {/* Grid lines */}
                              {getChartData().yAxisLabels.slice(1).map((_, i) => {
                                const totalLabels = getChartData().yAxisLabels.length - 1;
                                return (
                                  <div key={i} style={{ 
                                    position: 'absolute', 
                                    left: '0', 
                                    top: `${((i + 1) / totalLabels) * 100}%`, 
                                    width: 'calc(100% - clamp(40px, 8vw, 60px))', 
                                    borderTop: '1px solid rgba(0,0,0,0.15)' 
                                  }} />
                                );
                              })}
                            </div>
                            
                            {/* X-axis line */}
                            <div style={{ 
                              position: 'absolute', 
                              left: 'clamp(40px, 8vw, 60px)', 
                              top: 'calc(100% - 50px)', 
                              width: 'calc(100% - clamp(40px, 8vw, 60px))', 
                              borderBottom: '2px solid #000' 
                            }}></div>
                            
                            {/* Bars - positioned absolutely at bottom of Y-axis */}
                            <div style={{ 
                              position: 'absolute', 
                              left: 'clamp(50px, 10vw, 70px)', 
                              bottom: 'clamp(20px, 3vw, 35px)', 
                              width: 'calc(100% - clamp(50px, 10vw, 70px))', 
                              height: 'calc(100% - 50px)', 
                              display: 'flex', 
                              justifyContent: 'space-around', 
                              alignItems: 'flex-end' 
                            }}>
                              {getChartData().data.map((item, index) => {
                                const chartData = getChartData();
                                const chartHeight = 350; // Base height for calculations
                                const requiredHeight = (item.required / chartData.yMax) * chartHeight;
                                const actualHeight = (item.actual / chartData.yMax) * chartHeight;
                                
                                return (
                                  <div key={index} style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    width: 'clamp(120px, 20vw, 200px)', 
                                    position: 'relative' 
                                  }}>
                                    {/* Bar group */}
                                    <div className="chart-bar-group" style={{ gap: 'clamp(2px, 0.5vw, 5px)' }}>
                                      {/* Required bar */}
                                      <div className="chart-bar-required" style={{ 
                                        height: `${requiredHeight}px`,
                                        width: 'clamp(30px, 5vw, 40px)'
                                      }} />
                                      {/* Actual bar */}
                                      <div className="chart-bar-actual" style={{ 
                                        height: `${actualHeight}px`,
                                        width: 'clamp(30px, 5vw, 40px)'
                                      }} />
                                    </div>
                                    {/* X-axis label */}
                                    <div style={{ 
                                      marginTop: 'clamp(4px, 1vw, 8px)', 
                                      fontSize: 'clamp(7pt, 2vw, 9pt)', 
                                      textAlign: 'center', 
                                      color: '#000' 
                                    }}>{item.name}</div>
                                    {/* Data values below x-axis label - vertical layout */}
                                    <div className="chart-data-labels" style={{ 
                                      fontSize: 'clamp(6pt, 1.5vw, 8pt)',
                                      marginTop: 'clamp(2px, 0.5vw, 4px)'
                                    }}>
                                      <span>{item.required.toFixed(1)}</span>
                                      <span>{item.actual.toFixed(2)}</span>
                                    </div>
                                  </div>
                                );
                              })}
                                </div>
                              </div>
                          
                          {/* Y-axis label */}
                          <div style={{ 
                            position: 'absolute', 
                            left: 'clamp(20px, 4vw, 40px)', 
                            top: '45%', 
                            transform: 'rotate(-90deg)', 
                            transformOrigin: 'center', 
                            fontSize: 'clamp(8pt, 2vw, 10pt)', 
                            color: '#000' 
                          }}>
                            Strength N/mm2
                          </div>
                          
                          {/* Legend - Left Side (Lower Position) - Hidden on mobile */}
                          <div className="d-none d-md-block" style={{ 
                            position: 'absolute', 
                            left: '0px', 
                            top: 'clamp(400px, 60vw, 480px)', 
                            fontSize: 'clamp(7pt, 1.5vw, 9pt)', 
                            lineHeight: '1.8', 
                            color: '#000' 
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1vw, 8px)', marginBottom: 'clamp(4px, 1vw, 8px)' }}>
                              <div style={{ width: 'clamp(15px, 3vw, 20px)', height: 'clamp(10px, 2vw, 15px)', backgroundColor: '#4682B4' }} />
                              <span style={{ color: '#000', fontSize: 'clamp(6pt, 1.5vw, 8pt)' }}>Required strength N/mm2</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1vw, 8px)' }}>
                              <div style={{ width: 'clamp(15px, 3vw, 20px)', height: 'clamp(10px, 2vw, 15px)', backgroundColor: '#FFA500' }} />
                              <span style={{ color: '#000', fontSize: 'clamp(6pt, 1.5vw, 8pt)' }}>Actual Cube strength</span>
                            </div>
                          </div>
                          
                          {/* Legend - Bottom Center - Responsive */}
                          <div className="d-flex flex-column flex-md-row" style={{ 
                            marginTop: 'clamp(-10px, -2vw, -15px)', 
                            marginLeft: 'clamp(30px, 5vw, 60px)', 
                            justifyContent: 'center', 
                            gap: 'clamp(10px, 2vw, 20px)', 
                            fontSize: 'clamp(7pt, 1.5vw, 9pt)', 
                            color: '#000',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1vw, 8px)' }}>
                              <div style={{ width: 'clamp(20px, 4vw, 25px)', height: 'clamp(8px, 1.5vw, 12px)', backgroundColor: '#4682B4' }} />
                              <span style={{ color: '#000', fontSize: 'clamp(6pt, 1.5vw, 8pt)' }}>Required strength N/mm2</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 1vw, 8px)' }}>
                              <div style={{ width: 'clamp(20px, 4vw, 25px)', height: 'clamp(8px, 1.5vw, 12px)', backgroundColor: '#FFA500' }} />
                              <span style={{ color: '#000', fontSize: 'clamp(6pt, 1.5vw, 8pt)' }}>Actual Cube strength</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                
                 
                 {/* Complete Test Button */}
                 <div className="text-center mt-4">
                   <Button 
                     variant="success" 
                     size="lg" 
                     className="w-100 w-md-auto px-3 px-md-5 py-2 py-md-3"
                     style={{ 
                       fontSize: 'clamp(0.9rem, 3vw, 1.1rem)'
                     }}
                     onClick={async () => {
                       console.log('🚀 Complete Test button clicked!');
                       console.log('🚀 testRequestId:', testRequestId);
                       console.log('🚀 observationsData:', observationsData);
                       
                       // Always save current form values (including observation dropdowns) before navigating
                       if (testRequestId) {
                         try {
                           console.log('💾 USER CLICKED "COMPLETE TEST" - NOW SAVING CURRENT VALUES...');
                           const dataToSave = {
                             ...strengthData,
                             actual_7: strengthData.actual_7 || '',
                             actual_14: strengthData.actual_14 || '',
                             actual_28: strengthData.actual_28 || '',
                           };
                           
                           console.log('💾 Saving strength data (user-initiated save):', dataToSave);
                           await databaseService.saveStrengthGraphData(testRequestId, dataToSave);
                           console.log('✅ Strength data saved before completing test (user-initiated)');
                         } catch (error) {
                           console.error('⚠️ Failed to save strength data before completing test:', error);
                           // Continue anyway - don't block navigation
                         }
                       }
                       
                       // Skip sessionStorage for images - they're too large and cause quota errors
                       const capturedImages = observationsData?.capturedImages || {};
                       console.log('📸 Captured Images:', Object.keys(capturedImages));
                       console.log('📸 Skipping sessionStorage to avoid quota errors');
                       
                       navigate('/test-report-preview', { 
                         state: { 
                           testRequestId: testRequestId,
                           testData: { 
                             id: testRequestId,
                             ...formData, 
                             ...testData, 
                             ...observationsData,
                             strengthData, // Current field values
                             capturedImages: capturedImages
                           }
                         } 
                       });
                     }}
                   >
                     <i className="fas fa-clipboard-check me-2"></i> Complete Test
                   </Button>
                 </div>
              </Col>
            </Row>
          )}
          </Card.Body>
        </Card>

        {/* Error Modal - Auto-dismisses after 3 seconds */}
        <Modal 
          show={showErrorModal} 
          onHide={() => setShowErrorModal(false)}
          backdrop="static" 
          keyboard={false} 
          centered
        >
          <Modal.Header style={{ backgroundColor: '#dc3545', borderBottom: 'none' }}>
            <Modal.Title style={{ color: '#fff' }}>
              <i className="fas fa-exclamation-circle me-2"></i>Validation Error
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#1C2333', color: '#fff', textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-exclamation-circle fa-4x mb-3" style={{ color: '#dc3545' }}></i>
            <h4>{errorMessage}</h4>
            <p style={{ color: '#adb5bd' }} className="mt-3">This message will close automatically in 3 seconds.</p>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: '#1C2333', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Button 
              variant="outline-light" 
              onClick={() => setShowErrorModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Success Modal */}
        <Modal 
          show={showSuccessModal} 
          backdrop="static" 
          keyboard={false} 
          centered
        >
          <Modal.Header style={{ backgroundColor: '#28a745', borderBottom: 'none' }}>
            <Modal.Title style={{ color: '#fff' }}>
              <i className="fas fa-check-circle me-2"></i>Success!
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#1C2333', color: '#fff', textAlign: 'center', padding: '40px' }}>
            <i className="fas fa-check-circle fa-4x mb-3" style={{ color: '#28a745' }}></i>
            <h4>Graph Data Saved Successfully!</h4>
            <p style={{ color: '#adb5bd' }} className="mt-3">Your strength data and observations have been saved to the database.</p>
            <div className="mt-3">
              <div className="spinner-border" style={{ color: '#28a745' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ color: '#adb5bd' }} className="mt-2">Generating graph...</p>
            </div>
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default StrengthGraph;
